import { ESCALA, PALETA_SERIES, comAlfa, deveRotular, formatarValor } from "../tema";
import { estiloRotulo, opcoesBase } from "./base";

/**
 * Família 10 — área de evolução.
 *
 * Mesma leitura da linha, com o preenchimento degradê embaixo dando peso à
 * tendência: indicador mensal, preço ao longo da safra. Aceita mais de uma
 * série; as áreas ficam translúcidas para uma não esconder a outra.
 */
const modelo = {
  id: "area-evolucao",
  label: "Área de evolução",
  descricao: "Tendência ao longo do tempo com área preenchida. Ex.: indicador mensal do boi.",

  build(spec) {
    const base = opcoesBase(spec);
    const categorias = spec.dados?.categorias || [];
    const opcoesValor = spec.valores || {};
    const exibirEm = opcoesValor.exibirEm ?? "extremos";
    const suave = spec.extras?.suave !== false;

    return {
      ...base,
      chart: {
        ...base.chart,
        type: suave ? "areaspline" : "area",
        marginBottom: (spec.extras?.rodape ? 26 : 18) * ESCALA,
        marginLeft: 22 * ESCALA,
        marginRight: 22 * ESCALA,
      },
      xAxis: {
        ...base.xAxis,
        categories: categorias,
        lineWidth: 0,
        labels: {
          ...base.xAxis.labels,
          // Em séries longas os rótulos se atropelam; o Highcharts pula os
          // que não cabem, e o passo automático já resolve.
          autoRotation: false,
        },
      },
      yAxis: { ...base.yAxis, maxPadding: 0.2, minPadding: 0.06 },
      plotOptions: {
        ...base.plotOptions,
        series: {
          ...base.plotOptions.series,
          lineWidth: 3 * ESCALA,
          marker: { enabled: false },
          // Área tem threshold 0 por padrão, o que obriga o eixo a incluir
          // o zero: um indicador que vai de 312 a 356 vira uma reta colada
          // no topo. Sem threshold o eixo se ajusta aos dados e a área
          // preenche até a base do gráfico.
          threshold: null,
          dataLabels: {
            ...estiloRotulo(spec),
            y: -8 * ESCALA,
            formatter() {
              return formatarValor(this.y, opcoesValor);
            },
          },
        },
      },
      series: (spec.dados?.series || []).map((serie, i) => {
        const cor = serie.cor || PALETA_SERIES[i % PALETA_SERIES.length].cor;
        const valores = serie.valores || [];
        return {
          name: serie.nome,
          color: cor,
          fillColor: {
            linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
            stops: [
              [0, comAlfa(cor, spec.extras?.opacidadeArea ?? 0.55)],
              [1, comAlfa(cor, 0.02)],
            ],
          },
          data: valores.map((valor, j) => ({
            y: valor === null ? null : Number(valor),
            // Marcador só onde há rótulo: amarra a caixa ao ponto certo.
            marker: {
              enabled: deveRotular(j, valores.length, exibirEm),
              radius: 4 * ESCALA,
              fillColor: cor,
              lineColor: "#FFFFFF",
              lineWidth: 1.5 * ESCALA,
            },
            dataLabels: { enabled: deveRotular(j, valores.length, exibirEm) },
          })),
        };
      }),
    };
  },

  exemplo() {
    return {
      modelo: "area-evolucao",
      titulo: [
        { texto: "Boi Gordo", cor: "#F0F000" },
        { texto: " - Indicador São Paulo ", cor: "#FFFFFF" },
        { texto: "(R$/@)", cor: "#FFFFFF", tamanho: 8 },
      ],
      subtitulo: [{ texto: "Média mensal - 2026", cor: "#4EA6FF" }],
      fonte: "Fonte: IPAR RURAL BUSINESS",
      fundo: "#000000",
      fundoPlot: "nenhum",
      dados: {
        categorias: ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set"],
        series: [
          {
            nome: "Indicador",
            cor: "#3DAA73",
            valores: [312.4, 318.9, 325.1, 321.7, 330.2, 338.6, 344.9, 351.3, 356.8],
          },
        ],
      },
      legenda: { visivel: false },
      valores: { decimais: 2, prefixo: "", sufixo: "", exibirEm: "extremos" },
      rotulo: { corTexto: "#000000", corFundo: "#FFFFFF", corBorda: "#000000" },
      extras: { suave: true, opacidadeArea: 0.55 },
    };
  },
};

export default modelo;
